// ── Keyboard focus across an autosave (TPT9171) ──
//
// Settings controls save on `change` and stay disabled while their PATCH runs. Chrome moves focus
// off a disabled control to the body at its next rendering step, and enabling the control again
// does not bring it back, so a keyboard user would have to tab in from the top of the page after
// every change. syncPermissionModeSelect() (lib/agent-permission-mode.js) applies the same rule
// to the Permission mode picker, whose focusable trigger is not the input it saves.

// Disables `controls` (one control or an array of them) for a save and returns the function that
// enables them again. The control that had focus when the save started gets it back — after a
// success or a failure alike — but only while focus is still on the body: focus the user moved
// elsewhere during the save stays there. A save that re-renders its controls has replaced that
// control by then, so focus goes to the new element with the same id.
export function disableForSave(controls) {
  const list = Array.isArray(controls) ? controls : [controls];
  const focused = list.find(control => control === document.activeElement);
  for (const control of list) control.disabled = true;
  return () => {
    for (const control of list) control.disabled = false;
    const target = focused && (focused.isConnected ? focused : document.getElementById(focused.id));
    if (target && document.activeElement === document.body) target.focus();
  };
}

// ── Keyboard focus across a re-render (TPT9174) ──
//
// A view that rebuilds its markup after every write (the Settings Workflow status table) removes
// the control that had keyboard focus along with the old markup, and focus falls to the body.

// Notes the element inside `container` that has keyboard focus, before a render replaces the
// container's markup, and returns the function that focuses its copy in the new markup.
// `selectorFor(el)` names that copy, or returns null for an element that has none. Same rule as
// disableForSave(): the copy gets focus only while focus is still on the body, so focus the user
// moved elsewhere stays there. Focus outside `container`, or a copy the new markup lacks or has
// disabled, is left where it is.
export function noteFocusBeforeRender(container, selectorFor) {
  const focused = document.activeElement;
  const selector = container?.contains(focused) ? selectorFor(focused) : null;
  return () => {
    if (selector && document.activeElement === document.body) container.querySelector(selector)?.focus();
  };
}
